<template>
  <img
    ref="refImage"
    v-memo="[computedSource]"
    :src="computedSource"
    :class="[
      $props.class,
      {
        'cursor-pointer': lightbox,
      }
    ]"
    :alt="dataIsLoaded ? alt :''"
    :data-size="dataSize"
    :data-set-width="width"
    :data-set-height="height"
    @click="methodOpenLightbox"
    @load="methodLoaded"
    @error="methodError"
    >
  <Teleport v-if="dataShowLightbox && lightbox" to="#teleports">
    <div
      v-if="dataIsLoaded"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="dataShowLightbox = false"
      >
      <i
        class="absolute z-[80] top-4 right-4 text-gray-600 border-2 border-gray-600 bg-gray-200
        rounded-full aspect-square cursor-pointer fa-solid fa-xmark w-9 text-center align-middle content-center"
        @click="dataShowLightbox = false"
        />

      <!-- Added draggable and zoomable styling and handlers -->
      <PageBox
        class="my-[10vh]"
        :style="{ transform: `translate(${pageBoxPosition.x}px, ${pageBoxPosition.y}px) scale(${scale})` }"
        @mousedown="onPageBoxMouseDown"
        @wheel="onPageBoxWheel"
        @touchstart="onPageBoxTouchStart"
        >
        <img
          ref="refImageLightbox"
          :src="computedSource"
          class="w-full h-full object-contain p-[20vh]"
          :alt="alt"
          @dragstart.prevent
          >
      </PageBox>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  src?: string,
  height?: string|number,
  width?: string|number,
  rounding?: boolean,
  lightbox?: boolean,
  parameters?: {
    [key: string]: any
    size?: string,
  },
  alt?: string,
  nocache?: boolean,
  class?: string,
  clientasset?: boolean,
}>(),{
  rounding: true,
  lightbox: false,
})

const refImage = ref<HTMLImageElement | null>(null)
const dataIsLoaded = ref<boolean>(false)
const dataShowLightbox = ref<boolean>(false)
const pageBoxPosition = reactive({ x: 0, y: 0 }) // Reactive position for dragging
const scale = ref(1) // Reactive value for zoom

const dataSize = ref<string>('20x20');

let dragging = false
let startDrag = { x: 0, y: 0 }
let startOffset = { x: 0, y: 0 }

let initialPinchDistance = 0;
let initialPinchScale = 1;

let observerResize : ResizeObserver;
let observerIntersection : IntersectionObserver;

onMounted(() => {
  methodCalculateSize()

  observerResize = new ResizeObserver((entries) => {
    for (const entry of entries) {
      if (entry.target === refImage.value) {
        methodCalculateSizeDebounced()
      }
    }
  });

  observerIntersection = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      methodCalculateSizeDebounced()
      dataIsLoaded.value = true
      observerIntersection?.disconnect()
    }
  })

  if(props.lightbox){
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    // Add touch listeners for mobile
    window.addEventListener('touchmove', onTouchMove)
    window.addEventListener('touchend', onTouchEnd)

    methodSetDocumentScrollTo(true)
  }

  if(refImage.value) {
    observerResize.observe(refImage.value)
    observerIntersection.observe(refImage.value)
  }else{
    setTimeout(() => {
      if(refImage.value) {
        observerResize.observe(refImage.value)
        observerIntersection.observe(refImage.value)
      }
    }, 500)
  }
})

onUnmounted(() => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', onMouseUp)
  // Remove touch listeners
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)

  // Disconnect the observer
  if(refImage.value && observerResize) {
    observerResize.unobserve(refImage.value)
  }
})

const methodCalculateSize = () => {
  const width = (props.width as number|undefined) ?? refImage.value?.width
  const height = (props.height as number|undefined) ?? refImage.value?.height

  let result = null;

  if (width && height){
    result = `${width}x${height}`
  }

  if (!result && result == '20x20') {
    result = '20x20'
  }

  if(result != dataSize.value){
    dataSize.value = result
  }
}

const methodCalculateSizeDebounced = useDebounce(methodCalculateSize, 100)

const methodOpenLightbox = () => {
  if(!props.lightbox) return

  // set to center
  pageBoxPosition.x = 0
  pageBoxPosition.y = 0

  // set zoom
  scale.value = 1

  dataShowLightbox.value = true
}

const methodSetDocumentScrollTo = (allowed: boolean) => {
  document.body.style.overflow = (allowed) ? 'auto' : 'hidden'
  document.body.style.marginRight = (allowed) ? '0px': '15px'
}

const methodError = () => {
  console.warn('Error loading image:', computedSource.value)
  methodCalculateSize()
}

const methodLoaded = () => {
  dataIsLoaded.value = true
  methodCalculateSize()
}

const computedSource = computed(() => {
  const src = props.src
  const parameters = Object.assign({},props.parameters)

  if(props?.nocache){
    parameters.random = window.crypto.randomUUID()
  }

  const params = new URLSearchParams({
    ...parameters,
    rounding: props.rounding
      ? 'true'
      : 'false',
    clientasset: (src?.includes('http') && !props.clientasset
      ? 'false'
      : 'true'
    ),
    ...(props.parameters?.size
      ? {}
      : {
        size: (dataIsLoaded.value)
          ? dataSize.value
          : '50x50'
      }
    ),
    token: api?.bearerToken,
  })

  const result = (src?.includes('http'))
    ? src + '?' + params.toString()
    : api.getBackendUrl()+'warehouse/'+src+'?'+params.toString()

  return result;
})

function onPageBoxMouseDown(e: MouseEvent) {
  dragging = true
  startDrag = { x: e.clientX, y: e.clientY }
  startOffset = { x: pageBoxPosition.x, y: pageBoxPosition.y }
}

function onMouseMove(e: MouseEvent) {
  if (!dragging) return
  const dx = e.clientX - startDrag.x
  const dy = e.clientY - startDrag.y
  pageBoxPosition.x = startOffset.x + dx
  pageBoxPosition.y = startOffset.y + dy
}

function onMouseUp() {
  dragging = false
}

function onPageBoxWheel(e: WheelEvent) {
  if (!dragging) return
  e.preventDefault()
  const delta = -e.deltaY * 0.001
  scale.value = Math.min(3, Math.max(0.5, scale.value + delta))
}

// New functions to support touch dragging
function onPageBoxTouchStart(e: TouchEvent) {
  if (e.touches.length === 2) {
    e.preventDefault(); // Prevent page scroll during pinch
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    initialPinchDistance = Math.sqrt(dx * dx + dy * dy);
    initialPinchScale = scale.value;
    return;
  }
  if (e.touches.length !== 1) return; // single-touch only
  dragging = true;
  startDrag = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  startOffset = { x: pageBoxPosition.x, y: pageBoxPosition.y };
}

function onTouchMove(e: TouchEvent) {
  if (e.touches.length === 2) {
    e.preventDefault(); // Prevent page scroll during pinch
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const pinchDistance = Math.sqrt(dx * dx + dy * dy);
    const ratio = pinchDistance / initialPinchDistance;
    scale.value = Math.min(3, Math.max(0.5, initialPinchScale * ratio));
    return;
  }
  if (!dragging || e.touches.length !== 1) return;
  const dx = e.touches[0].clientX - startDrag.x;
  const dy = e.touches[0].clientY - startDrag.y;
  pageBoxPosition.x = startOffset.x + dx;
  pageBoxPosition.y = startOffset.y + dy;
}

function onTouchEnd() {
  dragging = false;
  initialPinchDistance = 0;
  initialPinchScale = scale.value;
}

watch(() => dataShowLightbox.value, (newValue) => {
  methodSetDocumentScrollTo(!newValue)
})

watch(refImage, () => {
  if(refImage.value) {
    if(observerResize){
      observerResize.disconnect()
      observerResize.observe(refImage.value)
    }
    methodCalculateSize()
  }
})
</script>
